import {
  Button,
  Variants as ButtonVariants,
  Icon,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'

import DeleteAllDocumentsModal from './DeleteAllDocumentsModal'
import DeleteOneDocumentModal from './DeleteOneDocumentModal'

interface Props {
  document: Document | null | undefined
  documents: Document[]
  attachment: Attachment
  onDelete: (documentIds: number[]) => void
  icon: React.ComponentProps<typeof Icon.Medium>['icon']
  type?: ButtonVariants
}

const BulkDeleteDocuments = ({ document, documents, attachment, onDelete, icon, type }: Props) => {
  const [open, toggle] = useToggle(false)
  const [openDeleteOne, toggleDeleteOne] = useToggle(false)
  const [openDeleteAll, toggleDeleteAll] = useToggle(false)

  const options: Option[] = useUnsafeMemo(() => {
    const deleteOne: Option = {
      type: 'wrong',
      onClick: toggleDeleteOne(true),
      title: i18n.t('documents.delete_one.button_label'),
    }

    const deleteAll: Option = {
      type: 'wrong',
      onClick: toggleDeleteAll(true),
      title: i18n.t('documents.delete_all.button_label'),
    }

    if (document && documents.length <= 1) return [deleteOne]

    if (document && documents.length > 1) return [deleteOne, deleteAll]

    return [deleteAll]
  }, [documents, document, toggleDeleteOne, toggleDeleteAll])

  const disabled = documents.length === 0

  return (
    <>
      <Popover attachment={attachment} targetAttachment="bottom center" toggle={toggle} open={open}>
        <Button onClick={toggle(true)} type={type} disabled={disabled} iconName={icon} />
        <PopoverContent onClose={toggle(false)} options={options} />
      </Popover>
      {openDeleteOne && document && (
        <DeleteOneDocumentModal
          document={document}
          onClose={toggleDeleteOne(false)}
          onDelete={onDelete}
        />
      )}
      {openDeleteAll && (
        <DeleteAllDocumentsModal documents={documents} onClose={toggleDeleteAll(false)} />
      )}
    </>
  )
}

export default observer(BulkDeleteDocuments)
